import React from "react";
import { SortableList } from "~/shared/ui/sortable-list";
import { Button } from "@racoon/ui";
import { IcPlus } from "@racoon/icons";
import { useTypedT, Namespace } from "@racoon/i18n";
import { SortableJobRow, JobIssue } from "./sortable-job-row";

interface SortableJobListProps {
  jobSlots: (number | null)[];
  onReorder: (fromIndex: number, toIndex: number) => void;
  onRemoveJobSlot: (slotIndex: number) => void;
  onOpenPicker: () => void;
  getJobDisplayName: (jobId: number | null) => string;
  getJobTypeLabel: (jobId: number) => string | undefined;
  getJobContainersInfo: (jobId: number) => string;
  getJobCustomerName: (jobId: number) => string;
  getJobVolumeText: (jobId: number) => string;
  getJobArrivalTimeInfo: (jobId: number) => {
    arrivalTimeString: string;
    comparison: { status: string; minutes: number; text: string } | null;
  } | null;
  trips: { id: string }[];
  activeTripId: string;
  onMoveJobToTrip: (jobId: number, targetTripId: string | null) => void;
  getJobIssue?: (jobId: number) => JobIssue | null;
  /** What a "duplicate" row says next to its number (see SortableJobRow). */
  getJobDuplicateNote?: (jobId: number) => string | undefined;
  /** Off where the route has no trips to move a job between (pseudo-routes). */
  showMoveToTrip?: boolean;
  /** Label of the trailing "add job" button; defaults to "add job to route". */
  addJobLabel?: string;
  /** Off where the route has no room for another job (single-job trucks). */
  canAddJob?: boolean;
  /** Shown in place of the add button while `canAddJob` is false. */
  addJobDisabledHint?: string;
}

// Stable id per slot: use the jobId for filled slots, position for empty ones.
const slotSortId = (jobId: number | null, index: number) =>
  jobId !== null ? `job-${jobId}` : `empty-${index}`;

export const SortableJobList: React.FC<SortableJobListProps> = ({
  jobSlots,
  onReorder,
  onRemoveJobSlot,
  onOpenPicker,
  getJobDisplayName,
  getJobTypeLabel,
  getJobContainersInfo,
  getJobCustomerName,
  getJobVolumeText,
  getJobArrivalTimeInfo,
  trips,
  activeTripId,
  onMoveJobToTrip,
  getJobIssue,
  getJobDuplicateNote,
  showMoveToTrip = true,
  addJobLabel,
  canAddJob = true,
  addJobDisabledHint,
}) => {
  const t = useTypedT(Namespace.Routes);

  const sortIds = jobSlots.map((jobId, index) => slotSortId(jobId, index));

  return (
    <div className="flex flex-1 flex-col">
      <SortableList ids={sortIds} onReorder={onReorder}>
        {jobSlots.map((jobId, slotIndex) => (
          <SortableJobRow
            key={sortIds[slotIndex]}
            sortId={sortIds[slotIndex]}
            displayNumber={slotIndex + 1}
            jobId={jobId}
            onOpenPicker={onOpenPicker}
            onRemoveJobSlot={() => onRemoveJobSlot(slotIndex)}
            getJobDisplayName={getJobDisplayName}
            getJobTypeLabel={getJobTypeLabel}
            getJobContainersInfo={getJobContainersInfo}
            getJobCustomerName={getJobCustomerName}
            getJobVolumeText={getJobVolumeText}
            getJobArrivalTimeInfo={getJobArrivalTimeInfo}
            trips={trips}
            activeTripId={activeTripId}
            onMoveJobToTrip={onMoveJobToTrip}
            getJobIssue={getJobIssue}
            getJobDuplicateNote={getJobDuplicateNote}
            showMoveToTrip={showMoveToTrip}
          />
        ))}
      </SortableList>

      {/* Add job row — the rail keeps the connector line running into the plus,
          so it reads as the next stop after the last job. */}
      <div className="flex items-stretch">
        <div className="flex w-12 shrink-0 flex-col items-center border-r border-slate-200">
          {/* min-h: the button is shorter than a job row, so without a floor the
              connector would collapse to nothing. */}
          <div className="mb-1 min-h-3 w-px flex-1 bg-text-body" />
          <IcPlus
            className={`h-5 w-5 shrink-0 ${canAddJob ? "text-brand-blue" : "text-text-secondary"}`}
          />
          {/* Invisible counterweight: keeps the plus centred like every pin. */}
          <div className="mt-1 min-h-3 w-px flex-1" />
        </div>
        {/* A disabled button would swallow its own tooltip (`disabled` turns off
            pointer events), so the reason takes the button's place instead. */}
        {canAddJob ? (
          <Button
            variant="ghost"
            className="text-brand-blue h-auto !py-2 text-sm font-medium hover:bg-transparent"
            onClick={onOpenPicker}
          >
            {addJobLabel ?? t("addJobToRoute")}
          </Button>
        ) : (
          <span className="flex items-center px-4 py-2 text-sm font-medium text-text-secondary">
            {addJobDisabledHint}
          </span>
        )}
      </div>

      {/* Filler: keeps the rail divider running to the bottom of the container */}
      <div aria-hidden className="flex flex-1">
        <div className="w-12 shrink-0 border-r border-slate-200" />
      </div>
    </div>
  );
};
